import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Herinvesteringsreserve,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, Observable, shareReplay, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-hir',
  templateUrl: './spec-hir.component.html',
  styleUrls: ['./spec-hir.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class SpecHirComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public year = new Date().getFullYear() - 1;
  public jaren: SelectFormControlItem<number>[] = [];

  public hirFg = new FormGroup({
    specificatieHir: new FormArray<FormGroup>([])
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get specificatieHir(): FormArray {
    return this.hirFg.get('specificatieHir') as FormArray;
  }

  addRij(): void {
    this.specificatieHir.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.specificatieHir.removeAt(index, { emitEvent: false });
    this.saveForm(false);
  }

  createFormGroup(item?: Herinvesteringsreserve): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      jaarVanVervreemding: new FormControl<number | null>(item?.jaarVanVervreemding ?? null),
      boekwinst: new FormControl<number | null>(item?.boekwinst ?? null),
      afschrijvingspercentage: new FormControl<number | null>(item?.afschrijvingspercentage ?? null),
      boekwaardeOpVervreemdingsmoment: new FormControl<number | null>(item?.boekwaardeOpVervreemdingsmoment ?? null),
    });
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      const lijst = data.aangifte?.balansEnResultatenrekening?.specificatieHerinvesteringsreserve ?? [];
      const fa = this.specificatieHir;
      while (fa.length > lijst.length) fa.removeAt(fa.length - 1);
      for (let i = 0; i < lijst.length; i++) {
        if (i < fa.length) {
          fa.at(i).patchValue(lijst[i], {emitEvent: false});
        } else {
          fa.push(this.createFormGroup(lijst[i]), {emitEvent: false});
        }
      }
    })
    this.jaren = Array.from({length: 5}, (_, k) => ({
      value: this.year - k,
      label: (this.year - k).toString(),
    }));
    this.hirFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // Keep new empty rows during editing so the user can fill them in.
      this.saveForm(true);
    });
  }

  ngOnDestroy(): void {
    // Cleanup fully empty rows when leaving the component.
    this.saveForm(false);
  }

  private heeftWaarde(row: Partial<Herinvesteringsreserve>): boolean {
    return !!(
      (typeof row.omschrijving === 'string' && row.omschrijving.trim()) ||
      row.jaarVanVervreemding != null ||
      row.boekwinst != null ||
      row.afschrijvingspercentage != null ||
      row.boekwaardeOpVervreemdingsmoment != null
    );
  }

  saveForm(includeLegeRijen = true): void {
    this.hirFg.updateValueAndValidity({emitEvent: false});
    const rijen = this.specificatieHir.getRawValue() as Partial<Herinvesteringsreserve>[];
    const rowsToPersist = includeLegeRijen ? rijen : rijen.filter((row) => this.heeftWaarde(row));
    const specificatieHerinvesteringsreserve: Herinvesteringsreserve[] = rowsToPersist
      .map((row) => ({
        omschrijving: row.omschrijving ?? undefined,
        jaarVanVervreemding: row.jaarVanVervreemding ?? undefined,
        boekwinst: row.boekwinst ?? undefined,
        afschrijvingspercentage: row.afschrijvingspercentage ?? undefined,
        boekwaardeOpVervreemdingsmoment: row.boekwaardeOpVervreemdingsmoment ?? undefined,
      } as unknown as Herinvesteringsreserve));

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) return currentEntity;
      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening: {
            ...currentEntity.aangifte.balansEnResultatenrekening,
            specificatieHerinvesteringsreserve,
          },
        } as VpbAangifte,
      };
    });
  }
}
